/**
 * SurgeryFilesCard.tsx
 *
 * The Surgery tab's file surface, replacing legacy's Google Drive panel.
 *
 * Legacy had TWO file surfaces on this tab and only one of them was Drive:
 *
 *   - "Additional Files (EKG, Medical Records, etc.)" already read the local
 *     docstore at documents_<account>/<patient>/medical_clearance — the SAME
 *     root PatientFilesController serves. So that card is a folder scope here,
 *     not a migration.
 *   - the Drive panel (consult-details-images2.php) is the part actually being
 *     replaced. Nothing from it is carried over: it refreshed an OAuth token on
 *     every render and printed the live access token into the page.
 *
 * Uses the shared patient file service so uploads, downloads and the >6MB
 * presigned path all behave exactly as they do on the Files tab.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2, Upload } from 'lucide-react';
import { Button, ConfirmDialog } from '@/components/ui';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { SURGERY_RETRY_OR_SUPPORT } from '@/pages/surgery/surgeryShared';
import { describeUploadFailures, uploadEach } from '@/pages/patients/files/uploadFailures';
import {
  deleteFile,
  downloadFile,
  listFiles,
  uploadFile,
  type PatientFile,
} from '@/services/patient-files.service';
import { ACCEPT } from '@/services/patientFileTypes';

/** The folder legacy's clearance card reads and writes. */
const CLEARANCE_FOLDER = 'medical_clearance';

interface SurgeryFilesCardProps {
  patientId: number;
}

function formatSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function SurgeryFilesCard({ patientId }: SurgeryFilesCardProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PatientFile | null>(null);

  const { data: files, isLoading, isError, error } = useQuery({
    queryKey: ['surgery-files', patientId, CLEARANCE_FOLDER],
    queryFn: () => listFiles(patientId, CLEARANCE_FOLDER),
    enabled: Number.isFinite(patientId) && patientId > 0,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['surgery-files', patientId, CLEARANCE_FOLDER] });

  const remove = useMutation({
    mutationFn: (file: PatientFile) =>
      deleteFile(patientId, file.name, CLEARANCE_FOLDER, file.source),
    onSuccess: () => {
      refresh();
      setPendingDelete(null);
    },
    onError: (err) =>
      toast.error('Could not delete the file', getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT)),
  });

  const onPick = async (fileList: FileList | null) => {
    if (!fileList?.length) return;

    const picked = Array.from(fileList);
    setUploading(true);
    try {
      const failures = await uploadEach(
        picked,
        (file) => uploadFile(patientId, file, CLEARANCE_FOLDER),
        SURGERY_RETRY_OR_SUPPORT,
      );
      if (failures.length === 0) {
        toast.success(picked.length === 1 ? 'File added' : 'Files added');
      } else {
        toast.error(
          failures.length === 1 ? 'Could not upload the file' : 'Could not upload some files',
          describeUploadFailures(failures, picked.length),
        );
      }
    } finally {
      // Refresh on failure too: files uploaded around a refused one are
      // saved, and must show in the list so they are not uploaded twice.
      refresh();
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Folders are not offered here — this card is a flat clearance-file list.
  const rows = (files ?? []).filter((f) => f.type !== 'folder');

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">
          Additional Files (EKG, Medical Records, etc.)
        </h3>
        <div className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Upload className="h-4 w-4" />}
            loading={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Upload
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            aria-label="Upload a file"
            onChange={(e) => void onPick(e.target.files)}
          />
        </div>
      </div>

      <div className="p-4">
        {isLoading ? (
          <p className="text-sm text-slate-400">Loading files…</p>
        ) : isError ? (
          <div
            role="alert"
            className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
          >
            {getErrorMessage(
              error,
              "Could not load this patient's files. Refresh the page, and if it keeps happening, contact Prospera support.",
            )}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-slate-400 italic">No files yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((file) => (
              <li key={`${file.source ?? ''}-${file.name}`} className="flex items-center gap-3 py-2">
                <span className="flex-1 truncate text-sm text-slate-800 max-md:min-w-0 max-md:whitespace-normal max-md:break-all" title={file.name}>
                  {file.name}
                </span>
                <span className="text-xs text-slate-400">{formatSize(file.size)}</span>
                <button
                  type="button"
                  className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 touch-target-44"
                  title={`Download ${file.name}`}
                  aria-label={`Download ${file.name}`}
                  onClick={() =>
                    void downloadFile(patientId, file.name, CLEARANCE_FOLDER, file.source)
                  }
                >
                  <Download className="h-4 w-4 touch-target-icon" />
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 touch-target-44"
                  title={`Delete ${file.name}`}
                  aria-label={`Delete ${file.name}`}
                  onClick={() => setPendingDelete(file)}
                >
                  <Trash2 className="h-4 w-4 touch-target-icon" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The Files tab uses window.confirm here; this is the house component. */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => (pendingDelete ? remove.mutateAsync(pendingDelete) : Promise.resolve())}
        title="Delete this file?"
        message={`"${pendingDelete?.name ?? ''}" will be removed from this patient's files. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </section>
  );
}
